import { ReactElement, SyntheticEvent, useContext, useState } from "react";
import {
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import {
  closeOutline,
  personOutline,
  logOutOutline,
  shareOutline,
  bugOutline,
  informationCircleOutline,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import Icon from "../../atoms/Icon";
import AuthContext from "../../../contexts/AuthContext";
import { useParams } from "react-router";
import MenuItem from "./MenuItem";
import LanguageSelectorModal from "../LanguageSelector/LanguageSelectorModal/LanguageSelectorModal";
import ReactCountryFlag from "react-country-flag";
import { langToCountryCode } from "../../../helper/lang";
import { resetCaches } from "../../../services/questionnaire.service";
import IslandSettings from "../../../pages/MentalPlusPages/IslandSettings";
import { settingsOutline } from "ionicons/icons";
import Profile from "../../../pages/Profile/Profile";
import ReportBugModal from "../../../pages/Profile/ReportBugModal";
import { useConfig } from "../../../contexts/ConfigContext";
import { getInvitations, getInvitPubLink } from "../../../services/invitation.service";
import IconProfile from "../../../assets/MentalPlus/Profil.svg";
import IconSettings from "../../../assets/MentalPlus/Paramètres.svg";
import IconDeconnexion from "../../../assets/MentalPlus/Déconnexion.svg";
import IconEquipe from "../../../assets/MentalPlus/Equipe.svg";
import IconContact from "../../../assets/MentalPlus/Contact.svg";
import IconPartager from "../../../assets/MentalPlus/Partager.svg";
import{
  InvitationChannelEnum,
  InvitationConfigType,
} from "../../../types/invitation.types";

interface MenuProps {
  isConnected: boolean;
  onClose(): void;
}

const Menu = ({ isConnected, onClose }: MenuProps): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { study_slug } = useParams<{ study_slug: string }>();
  const { user, endSession } = useContext(AuthContext);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [reportBugOpen, setReportBugOpen] = useState(false);
  const { study } = useConfig();
  const ionRouter = useIonRouter();

  const stopPropagation = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  const logOut = async () => {
    await endSession();
    resetCaches();
    ionRouter.push(`/${study_slug}/welcome`, "root", "replace", {
      unmount: true,
    });
  };

  const handleShare = async () => {
    if (!study) return;

    try {
      const { data: invitationConfigs } = await getInvitations({
        studyId: study.id,
        lang: language,
      });

      const publicLinkConfig = (invitationConfigs as InvitationConfigType[]).find(
        (config) => config.channel === InvitationChannelEnum.PUBLICLINK
      );

      if (!publicLinkConfig) {
        alert("Aucun lien d’invitation public n’est configuré.");
        return;
      }

      const { data: link } = await getInvitPubLink(
        { studyId: study.id },
        { invitation_config_id: publicLinkConfig.id }
      );

      const shareUrl = link as string;

      if (navigator.share) {
        try {
          await navigator.share({
            title: "Mental+",
            text: "Découvre Mental+ 🙂",
            url: shareUrl,
          });
        } catch (err) {
          console.error("Share aborted or failed:", err);
        }
      } else {
        try {
          await navigator.clipboard?.writeText(shareUrl);
          alert("Lien d’invitation Mental+ copié dans le presse-papiers ✅");
        } catch (err) {
          alert("Le partage n’est pas supporté sur cet appareil.");
          console.error("Clipboard share failed:", err);
        }
      }
    } catch (e) {
      console.error("Error while generating invitation token:", e);
      alert("Impossible de générer un lien d’invitation pour le moment.");
    }
  };

  const handleAbout = () => {
    onClose();
    ionRouter.push(`/${study_slug}/about`, "forward");
  };

  return (
    <Modal onClick={onClose}>
      <MenuList onClick={stopPropagation}>
        <Header>
          <Icon icon={closeOutline} size="large" onClick={onClose} />
        </Header>

        <div>
          {isConnected && (
            <MenuItem
              icon={<img src={IconProfile} style={{ width: 24, height: 24 }} />}
              title={t("mentalplus_menu_profile")}
              action={t("common_open")}
              onClick={() => setProfileOpen(true)}
            />
          )}

          {isConnected && (
            <MenuItem
             icon={<img src={IconSettings} style={{ width: 24, height: 24 }} />}
              title={t("mentalplus_menu_island_settings")}
              action={t("common_open")}
              onClick={() => setSettingsOpen(true)}
            />
          )}

          <MenuItem
            icon={
              <ReactCountryFlag
                countryCode={
                  langToCountryCode[language] || language.toUpperCase()
                }
                svg
                style={{
                  width: "1.5em",
                  height: "1.5em",
                  borderRadius: "3px",
                  objectFit: "cover",
                }}
                title={language}
              />
            }
            title={t(`lang_${language}`)}
            action={t("menu_language_button_text")}
            onClick={() => setModalVisible(true)}
          />

          <MenuItem
            icon={<img src={IconPartager} style={{ width: 24, height: 24 }} />}
            title={t("mentalplus_menu_share")}
            action={t("menu_share")}
            onClick={handleShare}
          />

          {isConnected && (
            <MenuItem
              icon={<img src={IconContact} style={{ width: 24, height: 24 }} />}
              title={t("mentalplus_menu_conatct")}
              action={t("common_open")}
              onClick={() => setReportBugOpen(true)}
            />
          )}

          <MenuItem
            icon={<img src={IconEquipe} style={{ width: 24, height: 24 }} />}
            title={t("mentalplus_menu_equipe")}
            action={t("common_open")}
            onClick={handleAbout}
          />

          {isConnected && (
            <MenuItem
              icon={<img src={IconDeconnexion} style={{ width: 24, height: 24 }} />}
              title={user?.attributes?.firstname || t("menu_logOut")}
              action={t("menu_logOut")}
              onClick={() => logOut().then().catch()}
            />
          )}

          <LanguageSelectorModal
            modalVisible={modalVisible}
            onCloseClick={() => setModalVisible(false)}
          />

          <IonModal
            className="resonance-sheetmodal"
            isOpen={settingsOpen}
            backdropDismiss={true}
            breakpoints={[0, 1]}
            initialBreakpoint={1}
            canDismiss={true}
            onDidDismiss={() => setSettingsOpen(false)}
          >
            <IonToolbar>
              <IonTitle>Island Setting</IonTitle>
              <CloseButton slot="end" onClick={() => setSettingsOpen(false)}>
                ×
              </CloseButton>
            </IonToolbar>

            <IslandSettings />
          </IonModal>

          <IonModal
            className="resonance-sheetmodal"
            isOpen={profileOpen}
            backdropDismiss={true}
            breakpoints={[0, 1]}
            initialBreakpoint={1}
            canDismiss={true}
            onDidDismiss={() => setProfileOpen(false)}
          >
            <IonToolbar>
              <IonTitle>Profile</IonTitle>
              <CloseButton slot="end" onClick={() => setProfileOpen(false)}>
                ×
              </CloseButton>
            </IonToolbar>

            <Profile />
          </IonModal>

          <ReportBugModal
            isOpen={reportBugOpen}
            onDismiss={() => setReportBugOpen(false)}
            user={user}
            study={study}
          />
        </div>
      </MenuList>
    </Modal>
  );
};

const Modal = styled.div`
  position: absolute;
  height: 100vh;
  width: 100vw;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: flex-end;
  z-index: 99999999;
`;

const slideAnimation = keyframes`
  from {
    margin-right: -70%;
  }
  to {
    margin-right: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const MenuList = styled.div`
  width: 70%;
  max-width: 400px;
  animation: ${slideAnimation} 0.1s ease;
  background-color: white;
`;

const Header = styled.div`
  height: 70px;
  background-color: ${(props) => props.theme.primary.background};
  color: ${(props) => props.theme.primary.contrast};
  display: flex;
  align-items: center;
`;

export default Menu;